<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, asList } from '../api/client'
import { normalizeResource, priceUnitLabels, resourceTypeLabels, serializeResource } from '../api/mappers'
import AppIcon from '../components/AppIcon.vue'
import AvitoCalendarDialog from '../components/AvitoCalendarDialog.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import EmptyState from '../components/EmptyState.vue'
import LoadingState from '../components/LoadingState.vue'
import ResourceDialog from '../components/ResourceDialog.vue'
import { useToast } from '../composables/useToast'
import { useSession } from '../composables/useSession'
import { canManageCatalog } from '../api/users'

const route = useRoute()
const router = useRouter()
const { show } = useToast()
const session = useSession()
const canManage = computed(() => canManageCatalog(session.user))

const resources = ref([])
const loading = ref(true)
const saving = ref(false)
const changingState = ref(false)
const dialogOpen = ref(false)
const editingResource = ref(null)
const calendarResource = ref(null)
const resourceToDeactivate = ref(null)
const search = ref('')
const typeFilter = ref('')
const activityFilter = ref('active')

const typeGroups = computed(() => {
  const groups = {}
  filteredResources.value.forEach((resource) => {
    if (!groups[resource.resourceType]) groups[resource.resourceType] = []
    groups[resource.resourceType].push(resource)
  })
  return Object.entries(groups)
})

const filteredResources = computed(() => {
  const term = search.value.trim().toLocaleLowerCase('ru')
  return resources.value.filter((resource) => {
    const matchesSearch = !term || [resource.name, resource.description]
      .some((value) => String(value || '').toLocaleLowerCase('ru').includes(term))
    const matchesType = !typeFilter.value || resource.resourceType === typeFilter.value
    const matchesActivity = activityFilter.value === 'all'
      || (activityFilter.value === 'active' ? resource.isActive : !resource.isActive)
    return matchesSearch && matchesType && matchesActivity
  })
})

const activeCount = computed(() => resources.value.filter((resource) => resource.isActive).length)
const totalCapacity = computed(() => resources.value.filter((resource) => resource.isActive).reduce((sum, resource) => sum + resource.capacity, 0))

function formatPrice(value) {
  if (value == null || value === '') return '—'
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(Number(value))
}

function rateLabel(resource) {
  if (resource.weekdayPrice == null && resource.weekendPrice == null) return 'Тариф не указан'
  const unit = priceUnitLabels[resource.priceUnit] || priceUnitLabels.per_booking
  if (resource.weekdayPrice === resource.weekendPrice) {
    return `${formatPrice(resource.weekdayPrice)} ₽ ${unit}`
  }
  return `Будни ${formatPrice(resource.weekdayPrice)} ₽ · вых. ${formatPrice(resource.weekendPrice)} ₽ ${unit}`
}

async function load() {
  loading.value = true
  try {
    const payload = await api.resources.list()
    resources.value = asList(payload, ['resources'])
      .map(normalizeResource)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  } catch (error) {
    show(error.message || 'Не удалось загрузить объекты.', 'error')
  } finally {
    loading.value = false
  }
}

function openCreate() {
  if (!canManage.value) return
  editingResource.value = null
  dialogOpen.value = true
}

function openEdit(resource) {
  if (!canManage.value) return
  editingResource.value = resource
  dialogOpen.value = true
}

async function saveResource(form) {
  if (!canManage.value || saving.value) return
  saving.value = true
  try {
    const payload = serializeResource(form)
    if (editingResource.value) {
      await api.resources.update(editingResource.value.id, payload)
      show('Объект обновлён.')
    } else {
      await api.resources.create(payload)
      show('Новый объект добавлен.')
    }
    dialogOpen.value = false
    await load()
  } catch (error) {
    show(error.message || 'Не удалось сохранить объект.', 'error')
  } finally {
    saving.value = false
  }
}

async function deactivate() {
  if (!canManage.value || !resourceToDeactivate.value) return
  changingState.value = true
  try {
    await api.resources.update(resourceToDeactivate.value.id, { is_active: false })
    show(`${resourceToDeactivate.value.name} деактивирован.`)
    resourceToDeactivate.value = null
    await load()
  } catch (error) {
    show(error.message || 'Не удалось деактивировать объект.', 'error')
  } finally {
    changingState.value = false
  }
}

async function reactivate(resource) {
  if (!canManage.value) return
  changingState.value = true
  try {
    await api.resources.update(resource.id, { is_active: true })
    show(`${resource.name} снова доступен для бронирования.`)
    await load()
  } catch (error) {
    show(error.message || 'Не удалось активировать объект.', 'error')
  } finally {
    changingState.value = false
  }
}

onMounted(async () => {
  await load()
  if (route.query.new === '1') {
    openCreate()
    const query = { ...route.query }
    delete query.new
    router.replace({ query })
  }
})
</script>

<template>
  <div class="page resources-page">
    <header class="page-header">
      <div>
        <span class="eyebrow">Инфраструктура</span>
        <h1>Объекты базы</h1>
        <p class="page-subtitle">Дома, бани, гриль-домики и банкетные залы.</p>
      </div>
      <button v-if="canManage" type="button" class="button button--primary" @click="openCreate">
        <AppIcon name="plus" :size="18" />
        Добавить объект
      </button>
    </header>

    <section class="resource-summary" aria-label="Сводка по объектам">
      <p><strong>{{ activeCount }}</strong><span>активных объектов</span></p>
      <p><strong>{{ totalCapacity }}</strong><span>гостей одновременно</span></p>
      <p><strong>{{ resources.length - activeCount }}</strong><span>неактивных объектов</span></p>
    </section>

    <section class="filter-bar filter-bar--resources" aria-label="Фильтры объектов">
      <label class="search-field">
        <AppIcon name="search" :size="18" />
        <span class="sr-only">Поиск объектов</span>
        <input v-model="search" type="search" placeholder="Название или описание" />
      </label>
      <div class="segmented-control" role="group" aria-label="Тип объекта">
        <button type="button" :class="{ active: typeFilter === '' }" @click="typeFilter = ''">Все</button>
        <button type="button" :class="{ active: typeFilter === 'house' }" @click="typeFilter = 'house'">Дома</button>
        <button type="button" :class="{ active: typeFilter === 'bathhouse' }" @click="typeFilter = 'bathhouse'">Бани</button>
        <button type="button" :class="{ active: typeFilter === 'grill_house' }" @click="typeFilter = 'grill_house'">Гриль</button>
        <button type="button" :class="{ active: typeFilter === 'banquet_hall' }" @click="typeFilter = 'banquet_hall'">Залы</button>
      </div>
      <label class="compact-field compact-field--end">
        <span class="sr-only">Активность</span>
        <select v-model="activityFilter">
          <option value="active">Только активные</option>
          <option value="inactive">Только неактивные</option>
          <option value="all">Все объекты</option>
        </select>
      </label>
    </section>

    <LoadingState v-if="loading" />

    <div v-else-if="typeGroups.length" class="resource-groups">
      <section v-for="([type, items], groupIndex) in typeGroups" :key="type" class="resource-group" :style="{ '--group-delay': `${groupIndex * 55}ms` }">
        <header class="resource-group__header">
          <h2>{{ resourceTypeLabels[type] }}</h2>
          <span>{{ items.length }}</span>
        </header>
        <div class="resource-list">
          <article v-for="resource in items" :key="resource.id" class="resource-row" :class="{ 'resource-row--inactive': !resource.isActive }">
            <span class="resource-accent" :style="{ backgroundColor: resource.color || 'var(--terracotta)' }" aria-hidden="true" />
            <div class="resource-row__main">
              <strong>{{ resource.name }}</strong>
              <p>{{ resource.description || 'Без дополнительного описания' }}</p>
              <small class="resource-rate">{{ rateLabel(resource) }}</small>
            </div>
            <div class="resource-capacity">
              <AppIcon name="users" :size="17" />
              <strong>{{ resource.capacity }}</strong>
              <span>чел.</span>
            </div>
            <span class="availability" :class="resource.isActive ? 'availability--free' : 'availability--inactive'">
              {{ resource.isActive ? 'Активен' : 'Неактивен' }}
            </span>
            <div v-if="canManage" class="row-actions">
              <button
                v-if="resource.resourceType === 'house'"
                type="button"
                class="button button--small button--secondary resource-ical-action"
                :aria-label="`Календарь Авито: ${resource.name}`"
                @click="calendarResource = resource"
              >
                <AppIcon name="calendar" :size="15" />
                <span>Авито iCal</span>
              </button>
              <button type="button" class="icon-button icon-button--bordered" title="Редактировать" :aria-label="`Редактировать ${resource.name}`" @click="openEdit(resource)">
                <AppIcon name="edit" :size="17" />
              </button>
              <button
                v-if="resource.isActive"
                type="button"
                class="icon-button icon-button--danger"
                title="Деактивировать"
                :aria-label="`Деактивировать ${resource.name}`"
                @click="resourceToDeactivate = resource"
              >
                <AppIcon name="ban" :size="17" />
              </button>
              <button v-else type="button" class="button button--small button--secondary" :disabled="changingState" @click="reactivate(resource)">
                Активировать
              </button>
            </div>
          </article>
        </div>
      </section>
    </div>

    <EmptyState v-else title="Объекты не найдены" text="Измените фильтры. Объекты и тарифы изменяет администратор.">
      <button v-if="canManage" type="button" class="button button--primary" @click="openCreate">Добавить объект</button>
    </EmptyState>

    <ResourceDialog
      v-if="canManage"
      :open="dialogOpen"
      :resource="editingResource"
      :saving="saving"
      @close="dialogOpen = false"
      @save="saveResource"
    />

    <AvitoCalendarDialog
      v-if="canManage"
      :open="Boolean(calendarResource)"
      :resource="calendarResource"
      @close="calendarResource = null"
    />

    <ConfirmDialog
      v-if="canManage"
      :open="Boolean(resourceToDeactivate)"
      title="Деактивировать объект?"
      :text="`Новые брони для «${resourceToDeactivate?.name || ''}» будут недоступны. История объекта и бронирований сохранится.`"
      confirm-label="Деактивировать"
      :pending="changingState"
      @close="resourceToDeactivate = null"
      @confirm="deactivate"
    />

  </div>
</template>
